import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import './SuccessPage.css';
import { buildDownloadUrl } from './downloadUrl';

function SuccessPage() {
  const location = useLocation();
  const {
    productName, filePath, orderID, purchaseDate, paymentAmount, purchaseId, productType,
  } = location.state || {};
  // An indicator cannot use the licence key at all — MT5 refuses WebRequest inside indicators —
  // so it gets an account-bound activation code instead of the EA flow and its whitelist steps.
  const isIndicator = productType === 'indicator';

  const [licenseKey, setLicenseKey] = useState('');
  const [email, setEmail] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [licenseSuccess, setLicenseSuccess] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');
  const [showLicenseInputs, setShowLicenseInputs] = useState(true);
  const [activationAccount, setActivationAccount] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [activationBusy, setActivationBusy] = useState(false);

  // Indicators skip the licence-key flow entirely. The licence still has to exist — it is what
  // proves the purchase — so it is created behind the scenes and immediately exchanged for an
  // activation code bound to the MT5 account the buyer names.
  const handleGetActivationCode = async (e) => {
    e.preventDefault();
    const digits = String(activationAccount).replace(/[^0-9]/g, '');
    if (!digits) {
      setLicenseError('Enter the MT5 account number (digits only).');
      return;
    }
    setActivationBusy(true);
    setLicenseError('');
    try {
      const licenseResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/licenses`, {
        purchaseId,
        orderId: orderID,
        email,
      });
      const issuedKey = licenseResponse.data?.licenseKey;
      if (!issuedKey) {
        setLicenseError('Could not prepare the license for this purchase.');
        return;
      }
      const token = localStorage.getItem('userToken');
      const codeResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/licenses/offline-code`,
        { licenseKey: issuedKey, accountNumber: digits, email },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      setActivationCode(codeResponse.data.code);
      setActivationAccount(codeResponse.data.accountNumber || digits);
    } catch (err) {
      setLicenseError(err.response?.data?.error || 'Failed to create activation code.');
    } finally {
      setActivationBusy(false);
    }
  };

  const handleCopyActivationCode = () => {
    if (!activationCode) return;
    navigator.clipboard.writeText(activationCode).then(() => {
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    });
  };

  const handleGenerateLicense = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/licenses`, {
        purchaseId,
        orderId: orderID,
        email,
      });
      if (response.data.success) {
        setLicenseKey(response.data.licenseKey);
        setLicenseSuccess('License generated successfully! Copy your key and use it in your EA or indicator.');
        setShowLicenseInputs(false);
        setLicenseError('');
      }
    } catch (err) {
      setLicenseError(err.response?.data?.error || 'Failed to generate license');
      setLicenseSuccess('');
    }
  };

  const handleCopyLicenseKey = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey).then(() => {
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(''), 2000);
      }).catch(() => {
        setCopyFeedback('Failed to copy.');
        setTimeout(() => setCopyFeedback(''), 2000);
      });
    }
  };

  return (
    <div className="success-page page-shell">
      <div className="success-header section-card">
        <span className="tag">Payment complete</span>
        <h1 className="page-title">Purchase successful</h1>
        <p className="page-lead">Your download is ready and your license can be generated below.</p>
        <div className="success-meta">
          {productName && <p><strong>Product:</strong> {productName}</p>}
          {paymentAmount && <p><strong>Amount paid:</strong> ${paymentAmount}</p>}
          {purchaseDate && <p><strong>Date:</strong> {purchaseDate}</p>}
          {orderID && <p><strong>Order ID:</strong> {orderID}</p>}
        </div>
        {filePath ? (
          <a href={buildDownloadUrl(filePath)} download className="primary-cta">
            Download Now
          </a>
        ) : (
          <p className="muted-text">Download link unavailable. Contact support.</p>
        )}
        <p className="muted-text">
          Re-download any time from the <Link to="/redownload">Re-download</Link> page.
        </p>
      </div>

      {isIndicator ? (
        <div className="license-section section-card">
          <h2>Activate your indicator</h2>
          <p className="muted-text">
            MetaTrader does not let an indicator check a licence over the internet, so this one
            activates with a code tied to a single MT5 account. Enter the account you will run it
            on and the code is generated instantly — it works offline, and you can request another
            any time you change accounts.
          </p>
          {!activationCode ? (
            <form onSubmit={handleGetActivationCode} className="form-grid">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Purchase email"
                required
              />
              <input
                type="text"
                inputMode="numeric"
                value={activationAccount}
                onChange={(e) => setActivationAccount(e.target.value)}
                placeholder="MT5 account number"
                required
              />
              <button type="submit" disabled={activationBusy}>
                {activationBusy ? 'Working...' : 'Get Activation Code'}
              </button>
            </form>
          ) : (
            <>
              <div className="license-key-container">
                <h3>Your activation code</h3>
                <div className="license-key-box">{activationCode}</div>
                <button onClick={handleCopyActivationCode} type="button">
                  {copyFeedback || 'Copy Activation Code'}
                </button>
              </div>
              <div className="activation-panel">
                <h3>How to activate in MT5</h3>
                <ol className="activation-steps">
                  <li>Download the file above and put it in <strong>MQL5 &gt; Indicators</strong>.</li>
                  <li>Restart MT5 or refresh the Navigator, then attach the indicator to a chart.</li>
                  <li>
                    Paste the code into the <strong>Activation Code</strong> input.
                  </li>
                  <li>Click <strong>OK</strong>. No internet permission or URL whitelist is needed.</li>
                </ol>
                <p className="muted-text">
                  This code only works on MT5 account <strong>{activationAccount}</strong>. Switching
                  accounts? Generate another from your dashboard at no cost.
                </p>
              </div>
            </>
          )}
          {licenseError && <p className="error-text">{licenseError}</p>}
        </div>
      ) : (
      <div className="license-section section-card">
        <h2>Generate license key</h2>
        <p className="muted-text">
          Licenses are linked to your purchase and limited to two devices per product.
          Your EA/indicator will bind the license automatically. Contact support if you need help.
        </p>
        {showLicenseInputs && (
          <form onSubmit={handleGenerateLicense} className="form-grid">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Purchase email"
              required
            />
            <button type="submit">Generate License</button>
          </form>
        )}
        {licenseError && <p className="error-text">{licenseError}</p>}
        {licenseSuccess && <p className="success-text">{licenseSuccess}</p>}
        {licenseKey && (
          <>
            <div className="license-key-container">
              <h3>Your license key</h3>
              <div className="license-key-box">{licenseKey}</div>
              <button onClick={handleCopyLicenseKey} type="button">
                {copyFeedback || 'Copy License Key'}
              </button>
            </div>

            <div className="activation-panel">
              <h3>How to activate in MT5</h3>
              <ol className="activation-steps">
                <li>Download the product file above and install it in the correct MT5 folder.</li>
                <li>In MT5, go to <strong>Tools &gt; Options &gt; Expert Advisors</strong>.</li>
                <li>Enable <strong>Allow WebRequest for listed URL</strong>.</li>
                <li>Add this URL exactly: <strong>https://www.vixalgotrading.com</strong></li>
                <li>Attach the EA or indicator to a chart.</li>
                <li>Paste your key into the <strong>LicenseKey</strong> input field.</li>
                <li>Click <strong>OK</strong> and allow the product to verify online.</li>
              </ol>
              <p className="muted-text">
                This license is valid for this product only and can be used on up to two MT5 installations/devices.
              </p>
            </div>
          </>
        )}
      </div>
      )}

      <div className="success-actions">
        <Link to="/products" className="ghost-cta">Browse more products</Link>
        <Link to="/dashboard" className="primary-cta">Go to dashboard</Link>
      </div>
    </div>
  );
}

export default SuccessPage;
